import { ERegionType, ERoomType } from '@/editor/data/type';
import { EFaceType } from '@/editor/entities/type';
import { globalStores } from '@/editor/stores';
import { EEntityType, ESceneMode } from '@/editor/types';
import {
  EParamGroupLabel,
  EParamLabel,
  EParamType,
  EParamUnit,
  EParamsKeys,
  IEditParams
} from '@/editor/types/editParamType';
import { startLayoutGenerate } from '@/editor/utils/entityOperation';
import { getAreaInfo } from '@/editor/utils/room/area';
import { ILayoutGenParamItem, IMaterialInfoData } from '@/pages/app/layout/tab-params/types';
import { ECommonUnit } from '@/types/enum';
import { LAYOUT_ALLOWED_REGION_TYPE } from '@/utils/constant';
import { REGION_NAME_BY_TYPE, ROOM_NAME_BY_TYPE } from '../const';
import { getParamTitle } from '../utils';

function getRoomOrRegionMaterialCfg(materialId: string) {
  return (
    globalStores.materialStore.getFloorMaterial(materialId) ??
    globalStores.materialStore.getDefaultMaterial(EFaceType.RegionFace)
  );
}

export function getRoomParamInfo(roomId: string) {
  const roomData = globalStores.roomStore.getRoomData(roomId);
  const roomRegions = globalStores.regionStore.getRoomRegions(roomId);

  const is2D = globalStores.sceneMode === ESceneMode.scene2d;
  if (roomData) {
    const { coreArea } = getAreaInfo(roomData);
    const { type, name } = roomData.roomInfo;
    const materialInfo = roomData.materialInfo;
    const currentMaterialCfg = getRoomOrRegionMaterialCfg(materialInfo.id);
    if (!currentMaterialCfg) return undefined;

    const genLayoutHandler = () => {
      startLayoutGenerate({ entityId: roomId, type: EEntityType.Room });
    };

    const paramDatas: IEditParams = {
      id: roomId,
      type: EEntityType.Room,
      title: getParamTitle(EEntityType.Room),
      group: [
        {
          id: EParamGroupLabel.FLOOR,
          label: EParamGroupLabel.FLOOR,
          fields: [EParamsKeys.material]
        },
        {
          id: EParamGroupLabel.TYPE,
          label: EParamGroupLabel.TYPE,
          fields: [EParamsKeys.type, EParamsKeys.name]
        },
        {
          id: EParamGroupLabel.AREA,
          label: EParamGroupLabel.AREA,
          fields: [EParamsKeys.coreArea]
        },
        {
          id: EParamGroupLabel.FLOORS,
          label: EParamGroupLabel.FLOORS,
          fields: [EParamsKeys.layerHeight]
        },
        ...(is2D
          ? [
              {
                id: EParamGroupLabel.EMPTY_LABEL,
                label: EParamGroupLabel.EMPTY_LABEL,
                hideLabel: true,
                fields: [EParamsKeys.layoutGen]
              }
            ]
          : [])
      ],
      values: {
        [EParamsKeys.type]: type,
        [EParamsKeys.name]: name,
        [EParamsKeys.coreArea]: coreArea,
        [EParamsKeys.layerHeight]: globalStores.paramStore.layerHeight,
        [EParamsKeys.material]: {
          thumbnail: currentMaterialCfg.display.thumbnail,
          price: currentMaterialCfg.properties.price,
          name: currentMaterialCfg.name,
          id: roomData.id,
          type: EEntityType.Region,
          unit: ECommonUnit.yenPerSqm,
          replaceable: roomRegions.length === 1 // 只有一个分区时才允许替换材质
        } satisfies IMaterialInfoData,
        [EParamsKeys.layoutGen]: {
          disabled: false,
          onClick: genLayoutHandler
        } satisfies ILayoutGenParamItem
      },
      fields: {
        [EParamsKeys.material]: {
          type: EParamType.MATERIAL_INFO,
          label: EParamLabel.ROOM_TYPE,
          settings: {
            hideLabel: true,
            readonly: false
          }
        },
        [EParamsKeys.type]: {
          type: EParamType.SELECT,
          label: EParamLabel.ROOM_TYPE,
          options: Object.entries(ERoomType)
            .filter(([key]) => Number.isNaN(Number(key)))
            .map(([_, value]) => {
              const type: ERoomType = Number(value);
              return { label: ROOM_NAME_BY_TYPE[type] || '', value: type, hide: type === ERoomType.Default };
            }),
          settings: {
            readonly: false
          }
        },
        [EParamsKeys.name]: {
          type: EParamType.TEXT,
          label: EParamLabel.ROOM_NAME,
          settings: {
            readonly: false,
            defaultValue: ROOM_NAME_BY_TYPE[ERoomType.Default],
            maxLength: 60
          }
        },
        [EParamsKeys.coreArea]: {
          type: EParamType.NUMBER,
          unit: EParamUnit.AREA,
          label: EParamLabel.CORE_AREA,
          settings: {
            readonly: true
          }
        },
        [EParamsKeys.layerHeight]: {
          type: EParamType.NUMBER,
          unit: EParamUnit.MM,
          label: EParamLabel.LAYER_HEIGHT,
          settings: {
            readonly: true
          }
        },
        [EParamsKeys.layoutGen]: {
          type: EParamType.LAYOUT_GEN,
          label: EParamLabel.ROOM_LAYOUT_GEN,
          settings: {
            readonly: false,
            hideLabel: true
          }
        }
      }
    };

    return paramDatas;
  }
  return undefined;
}

export function getRegionParamInfo(regionId: string) {
  const regionData = globalStores.regionStore.getRegionData(regionId);
  if (regionData) {
    const { coreArea } = getAreaInfo(regionData);
    const { type, name } = regionData.regionInfo;
    const { id } = regionData.materialInfo;
    const isShowLayoutGen = globalStores.sceneMode === ESceneMode.scene2d;
    const currentMaterialCfg = getRoomOrRegionMaterialCfg(id);
    if (!currentMaterialCfg) return undefined;

    const allowGenLayout = LAYOUT_ALLOWED_REGION_TYPE.includes(type);
    const genLayoutHandler = () => {
      if (allowGenLayout) {
        startLayoutGenerate({ entityId: regionId, type: EEntityType.Region });
      }
    };

    const paramDatas: IEditParams = {
      id: regionId,
      type: EEntityType.Region,
      title: EParamGroupLabel.SUBAREA,
      group: [
        {
          id: EParamGroupLabel.FLOOR,
          label: EParamGroupLabel.FLOOR,
          fields: [EParamsKeys.material]
        },
        {
          id: EParamGroupLabel.TYPE,
          label: EParamGroupLabel.TYPE,
          fields: [EParamsKeys.type, EParamsKeys.name]
        },
        {
          id: EParamGroupLabel.AREA,
          label: EParamGroupLabel.AREA,
          fields: [EParamsKeys.coreArea]
        },
        {
          id: EParamGroupLabel.FLOORS,
          label: EParamGroupLabel.FLOORS,
          fields: [EParamsKeys.layerHeight]
        },
        ...(isShowLayoutGen
          ? [
              {
                id: EParamGroupLabel.EMPTY_LABEL,
                label: EParamGroupLabel.EMPTY_LABEL,
                hideLabel: true,
                fields: [EParamsKeys.layoutGen]
              }
            ]
          : [])
      ],
      values: {
        [EParamsKeys.type]: type,
        [EParamsKeys.name]: name,
        [EParamsKeys.coreArea]: coreArea,
        [EParamsKeys.layerHeight]: globalStores.paramStore.layerHeight,
        [EParamsKeys.material]: {
          thumbnail: currentMaterialCfg.display.thumbnail,
          price: currentMaterialCfg.properties.price,
          name: currentMaterialCfg.name,
          id: regionData.id,
          type: EEntityType.Region,
          unit: ECommonUnit.yenPerSqm
        } satisfies IMaterialInfoData,
        [EParamsKeys.layoutGen]: {
          disabled: !allowGenLayout,
          onClick: genLayoutHandler
        } satisfies ILayoutGenParamItem
      },
      fields: {
        [EParamsKeys.material]: {
          type: EParamType.MATERIAL_INFO,
          label: EParamLabel.ROOM_TYPE,
          settings: {
            hideLabel: true,
            readonly: false
          }
        },
        [EParamsKeys.type]: {
          type: EParamType.SELECT,
          label: EParamLabel.SUBAREA_TYPE,
          options: Object.entries(ERegionType)
            .filter(([key]) => Number.isNaN(Number(key)))
            .map(([_, value]) => {
              const type: ERegionType = Number(value);
              return { label: REGION_NAME_BY_TYPE[type] || '', value: type, hide: type === ERegionType.Default };
            }),
          settings: {
            readonly: false
          }
        },
        [EParamsKeys.name]: {
          type: EParamType.TEXT,
          label: EParamLabel.SUBAREA_NAME,
          settings: {
            readonly: false,
            defaultValue: REGION_NAME_BY_TYPE[ERegionType.Default],
            maxLength: 60
          }
        },
        [EParamsKeys.coreArea]: {
          type: EParamType.NUMBER,
          unit: EParamUnit.AREA,
          label: EParamLabel.CORE_AREA,
          settings: {
            readonly: true
          }
        },
        [EParamsKeys.layerHeight]: {
          type: EParamType.NUMBER,
          unit: EParamUnit.MM,
          label: EParamLabel.LAYER_HEIGHT,
          settings: {
            readonly: true
          }
        },
        [EParamsKeys.layoutGen]: {
          type: EParamType.LAYOUT_GEN,
          label: EParamLabel.REGION_LAYOUT_GEN,
          settings: {
            readonly: false,
            hideLabel: true
          }
        }
      }
    };

    return paramDatas;
  }
  return undefined;
}
